import { gsap } from "gsap";

import {
  clearAnimationOverrides,
  setAnimationOverrides,
} from "@excalidraw/element";

import type { AnimatedValues } from "@excalidraw/element";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { sequenceAnimatedElements } from "./layout";
import { animationPresets } from "./presets";
import { CHAINED_CLIP } from "./presets/helpers";
import { createSpring } from "./spring";
import {
  getElementAnimations,
  getElementStartMode,
  MAX_ANIMATION_DURATION_MS,
} from "./types";

import type { ElementAnimation } from "./types";

/** Fully-seeded tween target. Every animatable key must EXIST on the
 * object (even as undefined): GSAP refuses to tween properties missing from
 * a plain-object target (`!(prop in target)` → "Missing plugin?" warning
 * and a silent no-op). undefined means "neutral" in the renderer. */
export const createAnimationTarget = (): AnimatedValues => ({
  dx: 0,
  dy: 0,
  angleDelta: 0,
  scaleX: 1,
  scaleY: 1,
  skewX: 0,
  skewY: 0,
  opacity: 1,
  hueRotate: 0,
  brightness: 1,
  saturate: 1,
  grayscale: 0,
  invert: 0,
  contrast: 1,
  sepia: 0,
  blur: 0,
  seedShift: 0,
  roughnessDelta: 0,
  gradientAngleDelta: 0,
  gradientOffsetShift: 0,
  gradientStopColors: undefined,
  anchorX: 0.5,
  anchorY: 0.5,
  fragProgress: undefined,
  emitProgress: undefined,
  counterProgress: undefined,
  textPathProgress: undefined,
  textPathOutline: undefined,
  revealProgress: undefined,
  textProgress: undefined,
  dashOffset: undefined,
  drawProgress: undefined,
  drawStart: undefined,
  strokeColor: undefined,
  backgroundColor: undefined,
  morphProgress: undefined,
  morphOutline: undefined,
  flowPhase: undefined,
  flowColor: undefined,
  // static channels written via setStatic() need NUMERIC seeds: GSAP's
  // generic property tween string-coerces the first render when the start
  // value is undefined ("0" instead of 0). Renderer gates all of these on the
  // progress channels above, so the seeds are inert until a preset runs.
  revealDir: 0,
  textEffect: 0,
  textStagger: 0.6,
  textIntensity: 1,
  textSplit: 0,
  morphShape: 0,
  flowCount: 3,
  flowSize: 2,
  revealHidden: 0,
  fragCount: 6,
  fragSpread: 1,
  fragGravity: 1,
  fragTwist: 1,
  fragShards: 0,
  emitKind: 0,
  emitCount: 24,
  emitSpread: 1,
  emitGravity: 1,
  emitSize: 1,
  counterFrom: 0,
  counterTo: 100,
  counterDecimals: 0,
  // 0 = "use the renderer's default dash pattern" (real values are >= 2)
  dashLen: 0,
  dashGap: 0,
});

/** Deterministic string+ints hash → [0, 1). Same shape as textEffects'
 * prand (packages can't import app code, so it lives here too). */
const hash01 = (seed: number, elementId: string, key: string) => {
  let h = (seed * 374761393) | 0;
  const s = `${elementId}\u0000${key}`;
  for (let i = 0; i < s.length; i++) {
    h = ((h ^ s.charCodeAt(i)) * 668265263) | 0;
  }
  h = ((h ^ (h >> 13)) * 1274126177) | 0;
  return ((h ^ (h >> 16)) >>> 0) / 4294967296;
};

const JITTER_SPAN = 0.3; // ±30% at jitter=1

/** Applies a clip's `variation` (seed + jitter) to its continuous numeric
 * values — duration, delay, and every schema param that isn't an enum,
 * toggle, or direction. Pure and deterministic per (seed, elementId, key):
 * the same scene always compiles the same timeline, so export is stable.
 * Returns the input object untouched when variation is absent. */
export const applyVariation = (
  cfg: ElementAnimation,
  elementId: string,
): ElementAnimation => {
  const variation = cfg.variation;
  if (!variation || variation.jitter <= 0) {
    return cfg;
  }
  const { seed, jitter } = variation;
  const scale = (key: string) =>
    1 + jitter * JITTER_SPAN * (hash01(seed, elementId, key) * 2 - 1);
  const preset = animationPresets[cfg.preset];
  const params = { ...cfg.params };
  for (const schema of preset?.paramsSchema ?? []) {
    const isToggle = schema.min === 0 && schema.max === 1 && schema.step === 1;
    if (
      schema.options ||
      isToggle ||
      schema.key === "direction" ||
      // pivot coords are a position, not a magnitude — jittering them moves
      // the anchor to a different spot on the shape
      schema.key === "pivotX" ||
      schema.key === "pivotY"
    ) {
      continue; // discrete semantics — scaling would change meaning
    }
    const value = params[schema.key];
    if (typeof value === "number" && Number.isFinite(value)) {
      const jittered = value * scale(schema.key);
      params[schema.key] = Math.min(schema.max, Math.max(schema.min, jittered));
    }
  }
  return {
    ...cfg,
    duration: Math.min(
      MAX_ANIMATION_DURATION_MS,
      Math.max(1, cfg.duration * scale("duration")),
    ),
    delay: Math.max(0, cfg.delay * scale("delay")),
    params,
  };
};

/** How each channel combines when several clips of one element animate it at
 * the same time (audit 5.5 — the additive layer stack). Channels not listed
 * here are grouped statics or last-defined-wins. */
const SUM_CHANNELS = [
  "dx",
  "dy",
  "angleDelta",
  "skewX",
  "skewY",
  "hueRotate",
  "blur",
  "seedShift",
  "roughnessDelta",
  "gradientAngleDelta",
  "gradientOffsetShift",
] as const;
const MULTIPLY_CHANNELS = [
  "scaleX",
  "scaleY",
  "opacity",
  "brightness",
  "saturate",
  "contrast",
] as const;
const MAX_CHANNELS = ["grayscale", "invert", "sepia", "revealHidden"] as const;
/** Progress channel → the static channels that only mean something for the
 * clip that owns that progress (the renderer gates the statics on it). The
 * owner is the LAST clip with a defined progress value. */
const OWNED_GROUPS: [keyof AnimatedValues, (keyof AnimatedValues)[]][] = [
  ["revealProgress", ["revealDir"]],
  ["textProgress", ["textEffect", "textStagger", "textIntensity", "textSplit"]],
  ["morphProgress", ["morphShape", "morphOutline"]],
  ["flowPhase", ["flowCount", "flowSize", "flowColor"]],
  ["drawProgress", ["drawStart"]],
  [
    "fragProgress",
    ["fragCount", "fragSpread", "fragGravity", "fragTwist", "fragShards"],
  ],
  [
    "emitProgress",
    ["emitKind", "emitCount", "emitSpread", "emitGravity", "emitSize"],
  ],
  ["counterProgress", ["counterFrom", "counterTo", "counterDecimals"]],
  ["textPathProgress", ["textPathOutline"]],
];
/** Undefined-neutral, no meaningful blend: the latest clip that set one wins. */
const LAST_WINS_CHANNELS = [
  "strokeColor",
  "backgroundColor",
  "gradientStopColors",
] as const;

/** Merges per-clip targets into the element's presented override object
 * (in place — the renderer holds its reference). */
export const composeTargets = (
  presented: AnimatedValues,
  targets: readonly AnimatedValues[],
) => {
  if (targets.length === 1) {
    Object.assign(presented, targets[0]);
    return;
  }
  const p = presented as Record<string, unknown>;
  for (const key of SUM_CHANNELS) {
    let sum = 0;
    for (const t of targets) {
      sum += (t[key] as number | undefined) ?? 0;
    }
    p[key] = sum;
  }
  for (const key of MULTIPLY_CHANNELS) {
    let product = 1;
    for (const t of targets) {
      product *= (t[key] as number | undefined) ?? 1;
    }
    p[key] = product;
  }
  for (const key of MAX_CHANNELS) {
    let max = 0;
    for (const t of targets) {
      max = Math.max(max, (t[key] as number | undefined) ?? 0);
    }
    p[key] = max;
  }
  for (const key of LAST_WINS_CHANNELS) {
    let value: unknown;
    for (const t of targets) {
      if (t[key] !== undefined) {
        value = t[key];
      }
    }
    p[key] = value;
  }
  // anchor: 0.5 is neutral (center) — the last clip that deliberately moved
  // it wins; a multi-clip element shares ONE pivot per frame by design
  {
    let ax = 0.5;
    let ay = 0.5;
    for (const t of targets) {
      if (t.anchorX !== undefined && t.anchorX !== 0.5) {
        ax = t.anchorX;
      }
      if (t.anchorY !== undefined && t.anchorY !== 0.5) {
        ay = t.anchorY;
      }
    }
    p.anchorX = ax;
    p.anchorY = ay;
  }
  // dashOffset is undefined-neutral but additive when present
  {
    let sum: number | undefined;
    for (const t of targets) {
      if (t.dashOffset !== undefined) {
        sum = (sum ?? 0) + t.dashOffset;
      }
    }
    p.dashOffset = sum;
  }
  for (const [progressKey, statics] of OWNED_GROUPS) {
    let owner: AnimatedValues | null = null;
    for (const t of targets) {
      if (t[progressKey] !== undefined) {
        owner = t;
      }
    }
    // no owner: progress stays undefined (renderer gate closed), statics
    // fall back to the last clip's seeds — inert by construction
    const source = owner ?? targets[targets.length - 1];
    p[progressKey] = owner ? owner[progressKey] : undefined;
    for (const key of statics) {
      p[key] = source[key];
    }
  }
};

/**
 * Compiles per-element animation configs into one paused GSAP timeline. Each
 * clip tweens its OWN plain AnimatedValues target (never elements); a compose
 * pass merges the clip targets into one presented override per element
 * (sum/multiply/max/last-wins per channel), so simultaneous clips layer
 * instead of colliding. The same timeline serves live preview (gsap ticker +
 * onUpdate redraw) and export (deterministic seek via sampleAt).
 */
export class AnimationRuntime {
  private timeline: gsap.core.Timeline | null = null;
  private overrides = new Map<string, AnimatedValues>();
  private clipTargets = new Map<string, AnimatedValues[]>();
  private playing = false;
  private paused = false;

  /** Recomputes every element's presented override from its clip targets. */
  private composeAll() {
    for (const [id, presented] of this.overrides) {
      const targets = this.clipTargets.get(id);
      if (targets && targets.length > 0) {
        composeTargets(presented, targets);
      }
    }
  }

  get isPlaying() {
    return this.playing;
  }

  get isPaused() {
    return this.paused;
  }

  /** Freezes playback in place (overrides stay registered, canvas keeps the
   * mid-animation state). */
  pause() {
    if (this.playing && this.timeline) {
      this.timeline.pause();
      this.paused = true;
    }
  }

  resume() {
    if (this.playing && this.paused && this.timeline) {
      this.paused = false;
      this.timeline.play();
    }
  }

  compile(elements: readonly ExcalidrawElement[]): number {
    this.dispose();
    this.overrides = new Map();
    this.clipTargets = new Map();
    const tl = gsap.timeline({ paused: true });

    // cross-element sequencing (element customData: animationStartMode /
    // animationOrder): "after" elements chain at the running global end,
    // "with" elements start alongside the previous element. Mirrored in
    // layout.ts (layoutElements) for the UI.
    const ordered = sequenceAnimatedElements(elements);
    let globalChainEnd = 0;
    let globalPrevStart = 0;
    for (const element of ordered) {
      const clips = getElementAnimations(element);
      // the renderer reads ONE override per element (`presented`); each clip
      // tweens its own target and composeAll() merges them per tick
      const presented = createAnimationTarget();
      const targets: AnimatedValues[] = [];
      let built = false;
      // (the first element's mode is moot: both cursors are still 0)
      const elementStart =
        getElementStartMode(element) === "after"
          ? globalChainEnd
          : globalPrevStart;
      // running end of this element's clip chain, and the start of the clip
      // added last — "with" clips reuse prevStart, "after" clips use chainEnd
      let chainEnd = elementStart;
      let prevStart = elementStart;
      for (let i = 0; i < clips.length; i++) {
        const cfg = applyVariation(clips[i], element.id);
        const preset = animationPresets[cfg.preset];
        if (!preset) {
          console.warn(
            `[animation] unknown preset "${cfg.preset}" on element ${element.id}`,
          );
          continue;
        }
        // a spring dictates both ease and duration (its settling time)
        const spring = cfg.spring ? createSpring(cfg.spring) : null;
        const effectiveCfg = {
          ...cfg,
          // JSON-invisible marker: presets disable immediateRender on chained
          // clips so their from-values don't render at the timeline's first
          // frame and poison earlier clips on the same channel
          [CHAINED_CLIP]: i > 0,
          // scenes can be edited outside the UI — never hand GSAP a
          // non-positive duration or negative delay
          duration: Math.max(1, cfg.duration),
          delay: Math.max(0, cfg.delay),
          // repeat -1 always compiles as a single cycle: GSAP caps an infinite
          // child at 1e10s, which would blow up chainEnd ("after" clips
          // scheduled centuries out), getDurationMs, and scrubbing. Looping is
          // the outer timeline's job (play({ loop: true })).
          ...(cfg.repeat === -1 ? { repeat: 0 } : {}),
          ...(spring
            ? { easing: spring.ease, duration: spring.durationMs }
            : {}),
        };
        // each clip builds onto its own sub-timeline (presets insert at
        // position 0), nested into the parent at the computed start — so no
        // preset needs to know about positioning
        const clipTl = gsap.timeline();
        const target = createAnimationTarget();
        try {
          preset.build(clipTl, target, element, effectiveCfg, elements);
        } catch (err) {
          console.error(
            `[animation] preset "${cfg.preset}" failed on element ${element.id}:`,
            err,
          );
          continue;
        }
        const position =
          i > 0 && cfg.startMode === "with" ? prevStart : chainEnd;
        tl.add(clipTl, position);
        prevStart = position;
        const clipDuration = clipTl.duration();
        if (!Number.isFinite(clipDuration)) {
          console.warn(
            `[animation] invalid duration in preset "${cfg.preset}" (${clipDuration}), skipping clip`,
          );
          tl.remove(clipTl);
          continue;
        }
        // endDelay pads the chain: "after" clips start later, and the marker
        // below keeps the pad inside the timeline's duration (loop breathing
        // room / export length)
        const endDelaySec = Math.max(0, cfg.endDelayMs ?? 0) / 1000;
        chainEnd = Math.max(chainEnd, position + clipDuration + endDelaySec);
        if (endDelaySec > 0) {
          tl.to({}, { duration: 0 }, chainEnd);
        }
        targets.push(target);
        built = true;
      }
      if (built) {
        this.overrides.set(element.id, presented);
        this.clipTargets.set(element.id, targets);
      }
      globalPrevStart = elementStart;
      globalChainEnd = Math.max(globalChainEnd, chainEnd);
    }

    // GSAP quirk (repo-wide, not preset-specific): a freshly-built paused
    // timeline's internal playhead already reads 0, so the very first
    // seek(0) any caller makes — sampleAt(0) for export, or play(0) — is a
    // no-op (GSAP short-circuits when the target time doesn't change from
    // the current one) and any position-0 tl.set()/setStatic() never
    // renders. Force one real render of position 0 right after building so
    // every target starts correct. This makes _tTime already 0, so a
    // caller's first seek(0) staying a no-op is harmless — it would render
    // the same (already-correct) values. Seeks to any other time, and every
    // seek after the first, are untouched: normal GSAP short-circuit rules
    // still apply to those.
    if (this.overrides.size > 0) {
      tl.render(0, true, true);
      this.composeAll();
    }

    this.timeline = tl;
    return this.overrides.size;
  }

  getDurationMs(): number {
    return (this.timeline?.duration() ?? 0) * 1000;
  }

  /** Current playback position within the cycle (drives the timeline playhead). */
  getTimeMs(): number {
    return (this.timeline?.time() ?? 0) * 1000;
  }

  play(onTick: () => void, opts: { loop?: boolean } = {}) {
    if (!this.timeline || this.overrides.size === 0) {
      return;
    }
    // respect prefers-reduced-motion accessibility setting
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    setAnimationOverrides(this.overrides);
    this.playing = true;
    this.paused = false;
    // re-assert ownership every tick: another runtime (e.g. an export's
    // sampleAt) may steal the global override slot mid-playback, which would
    // leave this timeline ticking against a renderer that no longer sees it
    this.timeline.eventCallback("onUpdate", () => {
      if (!this.timeline || !this.playing) {
        return;
      }
      this.composeAll();
      setAnimationOverrides(this.overrides);
      onTick();
    });
    // repeat/onComplete are set on BOTH branches — the same compiled timeline
    // can be replayed with a different `loop`, so stale state must not leak
    if (opts.loop) {
      // sprite-style ambient loop: replay the whole timeline forever (small
      // pause between cycles so one-shot presets read as loop cycles) until
      // stop() is called
      this.timeline.repeat(-1);
      this.timeline.repeatDelay(0.4);
      this.timeline.eventCallback("onComplete", null);
    } else {
      this.timeline.repeat(0);
      this.timeline.eventCallback("onComplete", () => {
        this.stop(onTick);
      });
    }
    this.timeline.play(0);
  }

  stop(onTick: () => void) {
    this.timeline?.pause();
    this.playing = false;
    this.paused = false;
    clearAnimationOverrides(this.overrides);
    onTick();
  }

  /** Deterministic sampling for export. Also registers the overrides map so
   * a subsequent render picks the sampled values up. */
  sampleAt(timeMs: number): Map<string, AnimatedValues> {
    if (this.timeline) {
      // seek() suppresses callbacks but still updates tween targets
      this.timeline.seek(timeMs / 1000);
      this.composeAll();
    }
    setAnimationOverrides(this.overrides);
    return this.overrides;
  }

  dispose() {
    clearAnimationOverrides(this.overrides);
    this.playing = false;
    this.paused = false;
    this.timeline?.kill();
    this.timeline = null;
  }
}
